import { ui } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp, Square, SquareCheckBig } from "lucide-react";
import { useState } from "react";
import type { CombinedEntry } from "../types";

interface EntriesListProps {
	entries: CombinedEntry[];
	count: number;
}

export function EntriesList({ entries, count }: EntriesListProps) {
	const [isExpanded, setIsExpanded] = useState(false);

	// Find the current entry index (first incomplete entry)
	const currentIndex = entries.findIndex((entry) => !entry.is_completed);

	// Get entries to display
	const getDisplayEntries = () => {
		if (isExpanded) return entries;

		const pastEntries = entries.slice(0, currentIndex).slice(-3); // Last 3 past entries
		const futureEntries = entries.slice(currentIndex).slice(0, 3); // First 3 future entries

		return [...pastEntries, ...futureEntries];
	};

	const displayEntries = getDisplayEntries();

	console.log("displayEntries", displayEntries);

	const hasMoreEntries = entries.length > displayEntries.length;
	const pastEntriesCount = currentIndex;
	const futureEntriesCount = entries.length - currentIndex;

	return (
		<div className="flex flex-col gap-2 w-full">
			<div className="flex justify-between items-center w-full">
				<span className="font-medium text-slate-700 dark:text-slate-200 text-sm">
					Entries
				</span>
				<span className="text-slate-500 dark:text-slate-400 text-xs">
					{count} remaining
				</span>
			</div>
			<div className="flex flex-col gap-2">
				{displayEntries.map((entry, index) => {
					const isPastEntry = index < currentIndex;
					const showEllipsis =
						!isExpanded &&
						((isPastEntry && index === 0 && currentIndex > 3) ||
							(!isPastEntry &&
								index === currentIndex &&
								entries.length - currentIndex > 3));

					return (
						<div key={`${entry.schedule_id}-${entry.schedule_index}`}>
							{showEllipsis && (
								<div className="flex justify-center items-center py-1">
									<span className="text-slate-400 text-sm">
										{isPastEntry
											? `+${pastEntriesCount - 3} more past entries`
											: `+${futureEntriesCount - 3} more future entries`}
									</span>
								</div>
							)}
							<div className="flex items-center gap-2 pl-4">
								{entry.is_completed ? (
									<SquareCheckBig size={20} className="text-green-600" />
								) : (
									<Square size={20} className="text-slate-800" />
								)}
								<span
									className={cn(
										"text-sm",
										entry.is_completed
											? "text-slate-500 dark:text-slate-400 line-through"
											: entry.is_locked
												? "text-slate-500 dark:text-slate-400"
												: "font-medium text-slate-900 dark:text-slate-100",
									)}
								>
									{entry.timing_label ||
										entry.label ||
										`Assessment #${(entry.schedule_index ?? 0) + 1}`}
								</span>
							</div>
						</div>
					);
				})}
				{(hasMoreEntries || isExpanded) && (
					<ui.Button
						variant="ghost"
						size="sm"
						className="flex justify-center items-center gap-2 w-full text-slate-500 hover:text-slate-700"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						{isExpanded ? (
							<>
								Show Less <ChevronUp size={16} />
							</>
						) : (
							<>
								Show More <ChevronDown size={16} />
							</>
						)}
					</ui.Button>
				)}
			</div>
		</div>
	);
}
